/**
 * Transaction Validation Utilities
 * 
 * Utility functions for validating transaction data
 * Uses Zod for schema validation
 */

import { z } from 'zod';
import type { CreateTransactionInput, UpdateTransactionInput } from '../types/transaction.types';

/**
 * Transaction type enum
 */
export const TransactionTypeEnum = z.enum(['debit', 'credit', 'transfer']);

/**
 * Transaction status enum
 */
export const TransactionStatusEnum = z.enum(['completed', 'pending', 'needs_review']);

export const TransactionGstStatusEnum = z.enum(['includes_gst', 'no_gst', 'unknown']);

/**
 * Create transaction input schema
 */
export const CreateTransactionSchema = z.object({
  accountId: z.string().uuid('Invalid account ID'),
  amount: z.number().positive('Amount must be positive'),
  description: z.string().min(1, 'Description is required'),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in ISO format (YYYY-MM-DD)'),
  type: TransactionTypeEnum,
  categoryId: z.string().uuid('Invalid category ID').optional(),
  merchantName: z.string().optional(),
  notes: z.string().max(500, 'Notes must be 500 characters or less').optional(),
  referenceNumber: z.string().optional(),
  payee: z.string().optional(),
  balanceAfter: z.number().optional(),
  sourceAccountId: z.string().uuid('Invalid source account ID').optional(),
  destinationAccountId: z.string().uuid('Invalid destination account ID').optional(),
  externalId: z.string().optional(),
  originalDescription: z.string().optional()
}).refine(
  (data) => {
    // If type is transfer, source and destination accounts must be provided
    if (data.type === 'transfer') {
      return data.sourceAccountId && data.destinationAccountId;
    }
    return true;
  },
  {
    message: 'Source and destination accounts are required for transfer transactions',
    path: ['sourceAccountId']
  }
).refine(
  (data) => {
    // Source and destination accounts must be different
    if (data.type === 'transfer' && data.sourceAccountId && data.destinationAccountId) {
      return data.sourceAccountId !== data.destinationAccountId;
    }
    return true;
  },
  {
    message: 'Source and destination accounts must be different',
    path: ['destinationAccountId']
  }
);

/**
 * Update transaction input schema
 */
export const UpdateTransactionSchema = z.object({
  categoryId: z.string().uuid('Invalid category ID').nullable().optional(),
  amount: z.number().positive('Amount must be positive').optional(),
  description: z.string().min(1, 'Description cannot be empty').optional(),
  /** null clears a previously set merchant name */
  merchantName: z.string().nullable().optional(),
  /** null clears previously set notes */
  notes: z.string().max(500, 'Notes must be 500 characters or less').nullable().optional(),
  referenceNumber: z.string().nullable().optional(),
  payee: z.string().nullable().optional(),
  balanceAfter: z.number().optional(),
  status: TransactionStatusEnum.optional(),
  flaggedForReview: z.boolean().optional(),
  statusReason: z.string().optional(),
  businessUsePercent: z
    .number()
    .min(0, 'Business use must be at least 0%')
    .max(100, 'Business use must be at most 100%')
    .nullable()
    .optional(),
  gstStatus: TransactionGstStatusEnum.nullable().optional(),
  receiptAttached: z.boolean().optional(),
});

/**
 * Validate create transaction input
 * @param input - Create transaction input
 * @returns Validation result
 */
export function validateCreateTransaction(input: CreateTransactionInput): {
  success: boolean;
  error?: z.ZodError;
} {
  const result = CreateTransactionSchema.safeParse(input);
  
  if (result.success) {
    return { success: true };
  }
  
  return {
    success: false,
    error: result.error
  };
}

/**
 * Validate update transaction input
 * @param input - Update transaction input
 * @returns Validation result
 */
export function validateUpdateTransaction(input: UpdateTransactionInput): {
  success: boolean;
  error?: z.ZodError;
} {
  const result = UpdateTransactionSchema.safeParse(input);
  
  if (result.success) {
    return { success: true };
  }
  
  return {
    success: false,
    error: result.error
  };
}

/** Aliases used by unit tests / older call sites */
export const validateCreateTransactionInput = validateCreateTransaction;
export const validateUpdateTransactionInput = validateUpdateTransaction;

/**
 * Validate date is not in the future (allow 1 day for pending transactions)
 * @param dateString - ISO date string
 * @returns True if date is valid
 */
export function validateDateNotFuture(dateString: string): boolean {
  const date = new Date(dateString);
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(23, 59, 59, 999);
  
  return date <= tomorrow;
}

